import { onUnmounted } from "vue";
import { toast } from "vue-sonner";

/** Keep finished progress toasts visible this long before auto-dismiss. */
export const PROGRESS_TOAST_DISMISS_DELAY_MS = 4_000;

/**
 * Schedule toast.dismiss after a delay. Prefer this over updating an Infinity toast
 * with a finite duration: sonner starts the close timer once when the toast mounts, so
 * a duration set by a later update never starts a timer. Also prefer it over
 * useDebounceFn as a timer.
 *
 * Supports multiple concurrent toast ids (e.g. parallel import batches).
 */
export function createScheduledToastDismiss(delayMs = PROGRESS_TOAST_DISMISS_DELAY_MS) {
  const timeouts = new Map<string | number, ReturnType<typeof setTimeout>>();

  function cancelScheduledDismiss(id?: string | number) {
    if (id !== undefined) {
      const existing = timeouts.get(id);
      if (existing !== undefined) {
        clearTimeout(existing);
        timeouts.delete(id);
      }
      return;
    }

    for (const timeoutId of timeouts.values()) {
      clearTimeout(timeoutId);
    }
    timeouts.clear();
  }

  function scheduleDismiss(id: string | number) {
    cancelScheduledDismiss(id);
    const timeoutId = setTimeout(() => {
      toast.dismiss(id);
      timeouts.delete(id);
    }, delayMs);
    timeouts.set(id, timeoutId);
  }

  return { scheduleDismiss, cancelScheduledDismiss };
}

export function useScheduledToastDismiss(delayMs = PROGRESS_TOAST_DISMISS_DELAY_MS) {
  const scheduler = createScheduledToastDismiss(delayMs);
  onUnmounted(() => scheduler.cancelScheduledDismiss());
  return scheduler;
}
